<template>
  <!-- Copy of engine-controls.vue. This component is used in older grid-view.vue,
    and will be eventually removed together with the old grid-view -->
  <div class="workflow-controls-wrapper">
    <div class="workflow-controls">
      <div class="d-flex workflow-buttons">
        <tooltip
          :value="engineButtonTooltipMsg"
          position="top-left"
          :disabled="engineCanRun && !calculatingScenarioElasticityResults && !hasUnsavedKpiResults"
        >
          <v-btn
            v-if="!isUnitManagerView"
            depressed
            :loading="calculatingScenarioResults"
            :disabled="!isEditable || !engineCanRun || calculatingScenarioElasticityResults"
            :class="[
              'refresh-button',
              'mr-2',
              `${hasUnsavedKpiResults && !calculatingScenarioResults ? 'unsaved-kpis' : ''}`,
            ]"
            data-dd-action-name="Grid view recalculate all"
            @click="recalculate()"
          >
            <cog-play-icon />
          </v-btn>
        </tooltip>

        <tooltip
          :value="$t('gridView.tooltips.hasUnsavedKpiResults')"
          position="top-left"
          :disabled="!hasUnsavedKpiResults"
        >
          <v-btn
            v-if="readFromDatabricks"
            outlined
            small
            depressed
            :disabled="!hasUnsavedKpiResults"
            :loading="savingKpiResults"
            :class="[
              'save-button',
              'mr-2',
              'pa-0',
              `${hasUnsavedKpiResults ? 'unsaved-kpis' : ''}`,
            ]"
            @click="onSaveKpiResultsToDatabricks"
          >
            {{ $t('gridView.saveChanges') }}
          </v-btn>
        </tooltip>
      </div>

      <div v-if="!isUnitManagerView" class="workflow-info-row">
        <last-triggered-info
          :last-triggered-info="get(lastTriggeredInfo.strategic_engine_full_run, scenario, {})"
          :loading="isLastTriggeredLoading.strategic_engine"
          source="strategic_engine_full_run"
          :width="200"
        />
      </div>
    </div>
    <feature-toggle v-if="!isUnitManagerView" :toggle="enablePriceOptimisation">
      <div class="workflow-controls">
        <div class="d-flex workflow-buttons">
          <tooltip
            :value="crossElasticityButtonTooltipMsg"
            position="top-left"
            :disabled="!calculatingScenarioResults && !hasUnsavedKpiResults && hasAOExtracts"
          >
            <v-btn
              depressed
              :loading="calculatingScenarioElasticityResults"
              :disabled="!canRunOptimisation"
              :class="['refresh-button', 'cross-elasticity-button', 'mr-2']"
              data-dd-action-name="Grid view cross-elasticity run"
              @click="runCrossElasticityOptimisation()"
            >
              <circles-play-icon />
            </v-btn>
          </tooltip>
        </div>

        <div class="workflow-info-row">
          <last-triggered-info
            v-if="hasAOExtracts"
            :last-triggered-info="get(lastTriggeredInfo.cross_elasticity_engine, scenario, {})"
            :loading="isLastTriggeredLoading.cross_elasticity_engine"
            source="cross_elasticity_engine"
            translation-time-key="gridView.optimisationLastRunTime"
            :width="200"
          />
        </div>
      </div>
    </feature-toggle>
    <feature-toggle v-if="!isUnitManagerView" :toggle="allowBulkPriceOverrides">
      <div class="workflow-controls">
        <div class="d-flex workflow-buttons">
          <tooltip
            :value="bulkActionsTooltip"
            position="top-left"
            :disabled="!isBulkActionsDisabled"
          >
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  small
                  :disabled="isBulkActionsDisabled"
                  :loading="bulkSelectionState.isLoading"
                  class="bulk-actions-button pa-0 mr-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small class="mr-1">mdi-menu-down</v-icon>
                  {{ $t('gridView.bulkActions.label') }}
                  <span v-if="bulkSelectionState.selectedCount > 0" class="ml-1">
                    ({{ bulkSelectionState.selectedCount }})
                  </span>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="item in bulkPriceOverrideMenuItems"
                  :key="item.action"
                  @click="onBulkPriceOverrideSelected(item.action)"
                >
                  <v-list-item-title>{{ $t(item.labelKey) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </tooltip>
        </div>
      </div>
    </feature-toggle>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { get, isEmpty, uniq } from 'lodash';

import {
  enablePriceOptimisation,
  allowBulkPriceOverrides,
  showAdditionalCostTypes,
} from '@enums/feature-flags';
import bulkPriceOverrideActions from '@enums/bulk-price-override-actions';
import featureFlagsMixin from '../../../mixins/featureFlags';

const bulkPriceOverrideConfig = [
  {
    action: bulkPriceOverrideActions.overrideWithEnginePrice,
    event: 'bulk-override-with-engine-price',
    labelKey: 'gridView.bulkActions.overrideWithProposed',
  },
  {
    action: bulkPriceOverrideActions.overrideWithIntentionPrice,
    event: 'bulk-override-with-intention-price',
    labelKey: 'gridView.bulkActions.overrideWithIntention',
  },
  {
    action: bulkPriceOverrideActions.overrideWithCostPriceDelta,
    event: 'bulk-override-with-cost-price-delta',
    labelKey: 'gridView.bulkActions.overrideWithCostPriceDelta',
  },
  {
    action: bulkPriceOverrideActions.overrideWithTransferPriceDelta,
    event: 'bulk-override-with-transfer-price-delta',
    labelKey: 'gridView.bulkActions.overrideWithTransferPriceDelta',
    featureFlag: showAdditionalCostTypes,
  },
  {
    action: bulkPriceOverrideActions.overrideWithPurchasePriceDelta,
    event: 'bulk-override-with-purchase-price-delta',
    labelKey: 'gridView.bulkActions.overrideWithPurchasePriceDelta',
    featureFlag: showAdditionalCostTypes,
  },
  {
    action: bulkPriceOverrideActions.removeAllOverrides,
    event: 'bulk-remove-all-overrides',
    labelKey: 'gridView.bulkActions.removeAllOverrides',
  },
];

export default {
  name: 'EngineControls',

  mixins: [featureFlagsMixin],

  props: {
    levelEntryKey: {
      type: String,
      required: true,
    },
    pricingGroupName: {
      type: String,
      default: '',
    },
    showToolStoreGroup: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    isUnitManagerView: {
      type: Boolean,
    },
  },

  data() {
    return {
      engineCanRun: false,
      engineBlockedMsg: '',
      crossElasticityBlockedMsg: '',
      enablePriceOptimisation,
      allowBulkPriceOverrides,
      get,
    };
  },

  computed: {
    ...mapState('clientConfig', ['toggleLogic']),
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapState('filters', ['scenario']),
    ...mapState('gridView', [
      'savingKpiResults',
      'calculatingScenarioResults',
      'recalculatingAll',
      'calculatingPgResults',
      'calculatingSpecificPgResults',
      'calculatingScenarioElasticityResults',
      'loadingItems',
      'bulkSelectionState',
      'lastTriggeredInfo',
      'isLastTriggeredLoading',
    ]),
    ...mapGetters('gridView', ['readFromDatabricks', 'itemOnPromotion', 'hasUnsavedKpiResults']),
    ...mapGetters('context', ['isWholesaleManager', 'isWholesaleAnalyst', 'isAdminLoggedIn']),

    scenariosWithUnsavedKpiResults() {
      return uniq(
        this.items.filter(item => item.hasUnsavedKpiResults).map(item => item.scenarioKey)
      );
    },

    isEditable() {
      return this.isAdminLoggedIn || (!this.isWholesaleManager && !this.isWholesaleAnalyst);
    },

    engineButtonTooltipMsg() {
      if (this.hasUnsavedKpiResults) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResults');
      }
      if (this.calculatingScenarioElasticityResults) {
        return this.$t('gridView.tooltips.crossElasticityOptimisationRunning');
      }
      return this.engineBlockedMsg;
    },

    crossElasticityButtonTooltipMsg() {
      if (this.hasUnsavedKpiResults) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResults');
      }
      if (this.calculatingScenarioResults) {
        return this.$t('gridView.tooltips.engineRunning');
      }
      if (!this.hasAOExtracts) {
        return this.$t('gridView.tooltips.missingAOExtracts');
      }
      return this.crossElasticityBlockedMsg;
    },

    hasAOExtracts() {
      return !isEmpty(get(this.selectedWorkpackage, 'aoExtracts', []));
    },

    canRunOptimisation() {
      return (
        this.isEditable &&
        !this.hasUnsavedKpiResults &&
        this.hasAOExtracts &&
        !this.calculatingScenarioResults
      );
    },

    bulkPriceOverrideMenuItems() {
      return this.filterArrayByFeatureFlag(bulkPriceOverrideConfig);
    },

    isBulkActionsDisabled() {
      return (
        this.bulkSelectionState.selectedCount === 0 ||
        this.loadingItems ||
        this.bulkSelectionState.hasLock ||
        this.bulkSelectionState.multiTsg ||
        this.bulkSelectionState.isLoading
      );
    },

    bulkActionsTooltip() {
      if (this.bulkSelectionState.selectedCount === 0) {
        return this.$t('gridView.bulkActions.noSelectionTooltip');
      }
      if (this.loadingItems) {
        return this.$t('gridView.bulkActions.loadingTooltip');
      }
      if (this.bulkSelectionState.hasLock) {
        return this.$t('gridView.bulkActions.lockedTooltip');
      }
      if (this.bulkSelectionState.multiTsg) {
        return this.$t('gridView.bulkActions.multiTsgTooltip');
      }
      if (this.bulkSelectionState.isLoading) {
        return this.$t('gridView.bulkActions.operationInProgressTooltip');
      }
      return '';
    },
  },

  async created() {
    // Check if engine can run
    const { canEngineRun, missingProduct, invalidProduct } = await this.canEngineRun({
      pricingGroupId: this.levelEntryKey,
    });
    this.engineCanRun = canEngineRun;

    if (!this.engineCanRun) {
      // Format message to inform user
      const getProductError = p =>
        this.showToolStoreGroup
          ? `${p.productKeyDisplay} ${this.$t('pricing.inStoregroup')}: ${
              p.toolStoreGroupDescription
            }`
          : p.productKeyDisplay;

      this.engineBlockedMsg = {
        [this.$t('pricing.error')]: this.$t('pricing.engineCannotRun', {
          pricingGroupName: this.pricingGroupName || '',
        }),
      };

      if (missingProduct) {
        this.engineBlockedMsg[this.$t('pricing.missing')] = getProductError(missingProduct);
      }
      if (invalidProduct) {
        this.engineBlockedMsg[this.$t('pricing.invalid')] = getProductError(invalidProduct);
      }
    }
  },

  methods: {
    ...mapActions('gridView', [
      'canEngineRun',
      'saveKpiResultsToDatabricks',
      'handleRecalculate',
      'handleCrossElasticityOptimisation',
    ]),

    async recalculate() {
      this.handleRecalculate({
        scenarioKey: this.scenario,
        workpackageId: this.selectedWorkpackage._id,
      });
    },

    async onSaveKpiResultsToDatabricks() {
      await this.saveKpiResultsToDatabricks({
        scenarioKeys: this.scenariosWithUnsavedKpiResults,
        levelEntryKey: this.levelEntryKey,
        isUnitManagerView: this.isUnitManagerView,
      });
    },

    async runCrossElasticityOptimisation() {
      this.handleCrossElasticityOptimisation({
        scenarioKey: this.scenario,
        workpackageId: this.selectedWorkpackage._id,
      });
    },

    onBulkPriceOverrideSelected(action) {
      this.$emit('bulk-action-selected', action);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.refresh-button {
  background-color: $pricing-primary !important;
  color: white;
  min-width: unset !important;
  padding: unset !important;
  height: 3rem !important;
  width: 3rem;

  &.unsaved-kpis {
    background-color: $pricing-orange !important;
  }
}

.cross-elasticity-button {
  background-color: $pricing-blue !important;
}

.save-button {
  &.theme--light.v-btn.v-btn--disabled {
    background-color: transparent;
    border: 1px solid $pricing-primary;
    color: $pricing-primary !important;
  }

  &.unsaved-kpis {
    background-color: $pricing-orange;
    border: none;
    color: white !important;
  }
}

.workflow-controls-wrapper {
  display: flex;
  .workflow-controls {
    .workflow-buttons {
      align-items: center;
    }

    .workflow-info-row {
      margin-top: 4px;
    }
  }
}

.bulk-actions-button.v-btn--outlined {
  border-color: $pricing-primary;
  color: $pricing-primary;

  &.v-btn--disabled {
    border-color: $pricing-grey !important;
    color: $pricing-grey-dark !important;
  }
}
</style>
